import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Trash2, MessageSquare, Activity } from "lucide-react";
import { useSession } from "@/App";

// CANONICAL WHITE WORDMARK
const WORDMARK_URL = "/havn-logo-white.png";
const BG_IMAGE_URL = "/assets/havn_bg_dark.png";

// Background style with soft glow and vignette
const BG_STYLE = {
  backgroundImage: `
    radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
    radial-gradient(circle at center, rgba(255,250,235,0.2) 0%, rgba(255,250,235,0.08) 25%, transparent 50%),
    url(${BG_IMAGE_URL})
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const { sessionLogger, conversationStorage } = useSession();
  
  const [activeTab, setActiveTab] = useState('conversations');
  const [conversations, setConversations] = useState([]);
  const [logs, setLogs] = useState([]);

  // Load data on mount
  useEffect(() => {
    setConversations(conversationStorage.getAll());
    setLogs(sessionLogger.getLogs());
  }, [conversationStorage, sessionLogger]);

  const handleExport = () => {
    const exportData = {
      conversations: conversations,
      sessionLogs: logs,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `havn-history-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    sessionLogger.log('history_exported');
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all history? This cannot be undone.')) {
      conversationStorage.clear();
      sessionLogger.clear();
      setConversations([]);
      setLogs([]);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString();
  };

  // Group conversations by date
  const groupedConversations = conversations.reduce((groups, msg) => {
    const date = formatDate(msg.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  // Get log type color
  const getLogColor = (type) => {
    if (type.includes('error')) return 'text-red-400';
    if (type.includes('success')) return 'text-[var(--sidekick-primary)]';
    if (type.includes('start')) return 'text-blue-400';
    if (type.includes('end')) return 'text-yellow-400';
    return 'text-[var(--sidekick-text-secondary)]';
  };

  return (
    <div className="havn-container" style={BG_STYLE} data-testid="history-page">
      <div className="havn-bg-gradient" aria-hidden="true" />
      
      {/* Header */}
      <header style={{
        position: 'absolute',
        top: '20px',
        left: '0',
        right: '0',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        boxSizing: 'border-box',
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/home')}
          className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          aria-label="Go back"
          data-testid="back-button"
        >
          <ArrowLeft size={24} className="text-[var(--sidekick-text-secondary)]" />
        </button>
        
        <img 
          src="/havn-logo-white.png"
          alt="HAVN"
          style={{ height: '28px', width: 'auto', objectFit: 'contain' }}
        />
        
        <button
          onClick={handleExport}
          className="p-2 rounded-full hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          aria-label="Export logs"
          data-testid="export-button"
        >
          <Download size={24} className="text-[var(--sidekick-primary)]" />
        </button>
      </header>

      {/* Tabs */}
      <div className="flex gap-2 px-4 z-10 mb-4">
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
            activeTab === 'conversations'
              ? 'bg-[var(--sk-primary)] text-[var(--sk-primary-foreground)]'
              : 'sk-glass-card text-[var(--sidekick-text-secondary)]'
          }`}
          data-testid="conversations-tab"
        >
          <MessageSquare size={18} />
          Conversations
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
            activeTab === 'logs'
              ? 'bg-[var(--sk-primary)] text-[var(--sk-primary-foreground)]'
              : 'sk-glass-card text-[var(--sidekick-text-secondary)]'
          }`}
          data-testid="logs-tab"
        >
          <Activity size={18} />
          System Logs
        </button>
      </div>

      {/* Content */}
      <main className="flex-1 z-10 px-4 overflow-y-auto custom-scrollbar pb-20">
        {activeTab === 'conversations' ? (
          <div className="space-y-6">
            {Object.keys(groupedConversations).length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare size={48} className="mx-auto text-[var(--sidekick-text-secondary)] opacity-50 mb-4" />
                <p className="text-[var(--sidekick-text-secondary)]">No conversations yet</p>
              </div>
            ) : (
              Object.entries(groupedConversations).map(([date, msgs]) => (
                <div key={date}>
                  <h3 className="text-sm font-medium text-[var(--sidekick-text-secondary)] mb-3 px-1">
                    {date}
                  </h3>
                  <div className="space-y-2">
                    {msgs.map((msg) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-4 rounded-xl ${
                          msg.role === 'user'
                            ? 'bg-[rgba(var(--sk-primary-rgb),0.1)] ml-8'
                            : 'sk-glass-card mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className={`text-xs font-medium ${
                            msg.role === 'user' ? 'text-[var(--sidekick-primary)]' : 'text-[var(--sidekick-text-secondary)]'
                          }`}>
                            {msg.role === 'user' ? 'You' : 'HAVN'}
                          </span>
                          <span className="text-xs text-[var(--sidekick-text-secondary)]">
                            {formatTime(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-[var(--sidekick-text-primary)] text-sm leading-relaxed">
                          {msg.content}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {logs.length === 0 ? (
              <div className="text-center py-12">
                <Activity size={48} className="mx-auto text-[var(--sidekick-text-secondary)] opacity-50 mb-4" />
                <p className="text-[var(--sidekick-text-secondary)]">No logs yet</p>
              </div>
            ) : (
              [...logs].reverse().slice(0, 100).map((log, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-3 rounded-lg sk-glass-card font-mono text-xs"
                >
                  <div className="flex justify-between items-start gap-4">
                    <span className="text-[var(--sidekick-text-secondary)] shrink-0">
                      {formatTime(log.timestamp)}
                    </span>
                    <span className={`font-medium shrink-0 ${getLogColor(log.type)}`}>
                      {log.type}
                    </span>
                  </div>
                  {Object.keys(log).filter(k => !['timestamp', 'type'].includes(k)).length > 0 && (
                    <div className="mt-2 text-[var(--sidekick-text-secondary)] break-all">
                      {Object.entries(log)
                        .filter(([k]) => !['timestamp', 'type'].includes(k))
                        .map(([k, v]) => (
                          <div key={k}>
                            <span className="text-[var(--sidekick-primary)]">{k}:</span>{' '}
                            {typeof v === 'string' ? v.substring(0, 100) : JSON.stringify(v)}
                          </div>
                        ))
                      }
                    </div>
                  )}
                </motion.div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Clear All Button */}
      {(conversations.length > 0 || logs.length > 0) && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[var(--sk-bg-fallback)] to-transparent z-20">
          <button
            onClick={handleClearAll}
            className="w-full max-w-md mx-auto py-3 px-6 sk-glass-card text-red-400
                     flex items-center justify-center gap-2 hover:bg-[rgba(255,255,255,0.12)]
                     transition-colors"
            data-testid="clear-all-button"
          >
            <Trash2 size={18} />
            Clear All History
          </button>
        </div>
      )}
    </div>
  );
}
